class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          
          // Add product index to dynamically loaded recommendations
          const productItems = this.querySelectorAll('.resource-list__item');
          productItems.forEach((item, index) => {
            item.setAttribute('data-product-index', index + 1);
          });
          
          // Initialize wishlist buttons for dynamically loaded products
          this.#initializeWishlistButtons();
          
          // Update loading attributes for proper eager/lazy loading
          const isMobile = window.innerWidth < 750;
          const eagerCount = isMobile ? 2 : 5;
          
          // Find all product images in recommendations
          const productImages = this.querySelectorAll('.resource-list__item .recommendation-image');
          let lazyImagesList = [];
          
          productImages.forEach((img, index) => {
            const position = index + 1;
            if (position <= eagerCount) {
              // Load eager images immediately
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.srcset = img.dataset.srcset || '';
                delete img.dataset.src;
                delete img.dataset.srcset;
                img.loading = 'eager';
                img.setAttribute('fetchpriority', 'high');
                console.log('[Recommendations] Loaded product #' + position + ' immediately (eager)');
              }
            } else {
              // Keep lazy images for intersection observer
              lazyImagesList.push(img);
              img.loading = 'lazy';
              img.setAttribute('fetchpriority', 'auto');
              console.log('[Recommendations] Set product #' + position + ' for lazy loading');
            }
          });
          
          // Set up intersection observer for lazy images
          if (lazyImagesList.length > 0 && typeof IntersectionObserver !== 'undefined') {
            const imageObserver = new IntersectionObserver((entries) => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  const img = entry.target;
                  if (img.dataset.src) {
                    const position = Array.from(productImages).indexOf(img) + 1;
                    img.src = img.dataset.src;
                    img.srcset = img.dataset.srcset || '';
                    delete img.dataset.src;
                    delete img.dataset.srcset;
                    console.log('[Recommendations] Product #' + position + ' loaded (entered viewport)');
                  }
                  imageObserver.unobserve(img);
                }
              });
            }, { rootMargin: '100px' });
            
            lazyImagesList.forEach(img => imageObserver.observe(img));
          }
          
          console.log('[Recommendations] Setup complete: ' + eagerCount + ' eager, ' + lazyImagesList.length + ' lazy');
          
          // Set up lazy loading for dynamically loaded images
          // First, check if any images don't have src but have data-src
          const lazyImages = this.querySelectorAll('img.lazy-image[data-src], img[data-src]:not([src]), .product-media__image-placeholder');
          console.log('[Recommendations] Found', lazyImages.length, 'lazy images/placeholders');
          
          // Handle placeholders that need to be replaced with images
          const placeholders = this.querySelectorAll('.product-media__image-placeholder');
          placeholders.forEach((placeholder, index) => {
            const productCard = placeholder.closest('.resource-list__item');
            const position = productCard ? parseInt(productCard.getAttribute('data-product-index') || index + 1) : index + 1;
            const isEager = position <= 2; // First 2 products eager on mobile
            
            // For now, just load all images since they're not showing
            const mediaId = placeholder.getAttribute('data-media-id');
            console.log('[Recommendations] Processing placeholder for position', position, 'media ID:', mediaId);
            
            // We need to trigger loading for these somehow
            // Since we can't run the inline scripts, let's just show a basic image
            const img = document.createElement('img');
            img.className = 'product-media__image';
            img.alt = 'Product image';
            img.loading = isEager ? 'eager' : 'lazy';
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
            
            // We need to get the image URL somehow - let's check if there's a fallback
            const parentLink = placeholder.closest('a');
            if (parentLink) {
              // Try to extract product handle from the link
              const href = parentLink.href;
              console.log('[Recommendations] Found parent link:', href);
            }
            
            placeholder.textContent = 'Image placeholder - loading issue';
          });
          
          // Set up intersection observer for images with data-src
          const imagesWithDataSrc = this.querySelectorAll('img[data-src]');
          if (imagesWithDataSrc.length > 0 && typeof IntersectionObserver !== 'undefined') {
            const imageObserver = new IntersectionObserver((entries) => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  const img = entry.target;
                  if (img.dataset.src) {
                    img.src = img.dataset.src;
                    if (img.dataset.srcset) {
                      img.srcset = img.dataset.srcset;
                    }
                    delete img.dataset.src;
                    delete img.dataset.srcset;
                    img.classList.remove('lazy-image');
                    console.log('[Recommendations] Image lazy loaded for product at position', 
                      img.closest('.resource-list__item')?.getAttribute('data-product-index') || 'unknown');
                  }
                  imageObserver.unobserve(img);
                }
              });
            }, { rootMargin: '100px' });
            
            imagesWithDataSrc.forEach(img => imageObserver.observe(img));
            console.log('[Recommendations] Set up lazy loading for', imagesWithDataSrc.length, 'images with data-src');
          }
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  /**
   * Initialize wishlist buttons for dynamically loaded products
   */
  #initializeWishlistButtons() {
    if (!window.WishlistManager) {
      console.warn('[Product Recommendations] WishlistManager not available');
      return;
    }

    // Find all wishlist buttons in the recommendations
    const wishlistButtons = this.querySelectorAll('[data-wishlist-button]');
    
    wishlistButtons.forEach(button => {
      const productId = button.getAttribute('data-product-id');
      const productHandle = button.getAttribute('data-product-handle');
      const productTitle = button.getAttribute('data-product-title');
      
      if (!productId) return;
      
      const icon = button.querySelector('[data-wishlist-icon]');
      const addLabel = button.querySelector('.wishlist-button__label--add');
      const removeLabel = button.querySelector('.wishlist-button__label--remove');
      
      // Update button state
      function updateButtonState() {
        const isInWishlist = window.WishlistManager.contains(productId);
        
        if (isInWishlist) {
          icon?.classList.remove('wishlist-heart-icon--empty');
          icon?.classList.add('wishlist-heart-icon--filled');
          button.setAttribute('aria-pressed', 'true');
          
          if (addLabel && removeLabel) {
            addLabel.style.display = 'none';
            removeLabel.style.display = 'inline';
          }
        } else {
          icon?.classList.remove('wishlist-heart-icon--filled');
          icon?.classList.add('wishlist-heart-icon--empty');
          button.setAttribute('aria-pressed', 'false');
          
          if (addLabel && removeLabel) {
            addLabel.style.display = 'inline';
            removeLabel.style.display = 'none';
          }
        }
      }
      
      // Set initial state
      updateButtonState();
      
      // Handle click event
      button.addEventListener('click', async function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent double clicks
        if (button.classList.contains('wishlist-button--loading')) return;
        
        button.classList.add('wishlist-button--loading');
        
        try {
          await window.WishlistManager.toggle(productId, productHandle, productTitle);
          updateButtonState();
          
          // Show toast notification (if function exists)
          if (typeof showWishlistToast === 'function') {
            const action = window.WishlistManager.contains(productId) ? 'added' : 'removed';
            showWishlistToast(action, productTitle);
          }
        } catch (error) {
          console.error('[Product Recommendations] Failed to toggle wishlist:', error);
        } finally {
          button.classList.remove('wishlist-button--loading');
        }
      });
      
      // Listen for wishlist updates from other buttons
      window.WishlistManager.on('added', function(data) {
        if (data.productId === productId) {
          updateButtonState();
        }
      });
      
      window.WishlistManager.on('removed', function(data) {
        if (data.productId === productId) {
          updateButtonState();
        }
      });
    });
    
    console.log('[Product Recommendations] Initialized', wishlistButtons.length, 'wishlist buttons');
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
